import type { LocaleResource } from './types';
import { STATIC_PAGE_DOCUMENTS } from './staticPages';

const resource = {
  staticPages: STATIC_PAGE_DOCUMENTS.fr,
  common: {
    loading: 'Chargement...',
    actions: {
      close: 'Fermer le menu',
      search: 'Rechercher',
      backHome: "Retour à l'accueil",
      login: 'Connexion',
      register: 'Créer un compte',
      explore: 'Explorer',
    },
    states: {
      noResults: 'Aucun résultat',
      noResultsFor: 'Aucun résultat pour « {{query}} ».',
    },
  },
  app: {
    skipToContent: 'Aller au contenu principal',
    loading: 'Chargement...',
    notFound: {
      message: "Cette page n'existe pas dans l'univers Skoleom.",
      cta: "Retour à l'accueil",
    },
  },
  header: {
    brandHome: 'Accueil',
    universe: 'Universe',
    navLabel: 'Navigation principale',
    searchPlaceholder: 'Rechercher',
    searchAria: 'Rechercher',
    language: {
      change: 'Changer la langue',
      zones: {
        international: 'International',
        europe: 'Europe et Royaume-Uni',
        americas: 'Amériques',
        mena: 'MENA',
        asiaPacific: 'Asie-Pacifique',
        africa: 'Afrique',
      },
    },
    actions: {
      settings: 'Paramètres',
      favorites: 'Favoris',
      cart: 'Panier',
      account: 'Compte utilisateur',
      openMenu: 'Ouvrir le menu',
      closeMenu: 'Fermer le menu',
    },
    account: {
      dashboard: 'Votre espace',
      orders: 'Vos commandes',
      favorites: 'Favoris',
      admin: 'Admin Console',
      adminBadge: 'Admin',
      logout: 'Déconnexion',
    },
    nav: {
      home: 'Accueil',
      stores: 'Boutique',
      music: 'Musique',
      subscriptions: 'Abonnements',
      everyone: 'Pour Tous',
      pros: 'Pour les Pros',
      news: 'Actualités',
      events: 'Événements',
      group: 'Groupe',
      search: 'Recherche',
      support: 'Assistance',
    },
  },
  cart: {
    title: 'Votre panier',
    itemCount: '{{count}} article(s)',
    empty: {
      title: 'Votre panier est vide',
      subtitle: 'Découvrez nos contenus shoppables',
    },
    summary: {
      subtotal: 'Sous-total',
      shipping: 'Livraison',
      free: 'Offerte',
      total: 'Total',
    },
    checkout: {
      cta: 'Payer avec Skoleom Pay',
      security: 'Paiement souverain et sécurisé · conforme RGPD',
    },
    aria: {
      decrease: 'Diminuer la quantité',
      increase: 'Augmenter la quantité',
      remove: 'Retirer cet article',
      close: 'Fermer le panier',
    },
  },
  skoleomPage: {
    nav: {
      ariaLabel: 'Navigation Skoleom Page',
      home: 'Accueil',
      homeMobile: 'Accueil',
      trends: 'Tendances',
      trendsMobile: 'Tendances',
      create: 'Créez votre SeContent',
      createMobile: 'Créer',
      profile: 'Profil',
      profileMobile: 'Profil',
    },
    explorer: {
      poweredBy: 'Propulsé par SeSync',
      heroBefore: 'Regardez. Cliquez. ',
      heroHighlight: 'Découvrez.',
      heroSubtitle:
        "Une seule recherche, tout l'écosystème Skoleom Universe — vidéos, musique, médias, shopping. Chaque résultat devient une porte d'entrée immersive.",
      logoAlt: 'Skoleom Page',
      searchPlaceholder: 'Recherche un artiste, un film, une marque…',
      searchAria: 'Terme de recherche',
      searchSubmit: 'Lancer la recherche',
      searchReset: 'Réinitialiser la recherche',
      exploreCta: 'Explorer',
      resultsFor: 'Résultats pour',
      resultsOnPlatforms_one: 'sur {{count}} plateforme',
      resultsOnPlatforms_other: 'sur {{count}} plateformes',
      filter: 'Filtrer',
      platforms: 'Plateformes',
      filterPlatformsAria: 'Filtrer les plateformes',
      selectAll: 'Tout',
      selectNone: 'Aucun',
      loading: "Connexion à l'univers Skoleom…",
      noResults:
        'Aucun résultat ne correspond à vos filtres actuels. Sélectionnez plus de plateformes.',
    },
    actionCards: {
      sectionBefore: "Découvrez l'",
      sectionHighlight: 'univers Skoleom',
      mobile: {
        title: 'Mobile',
        description: 'Disponible sur iOS et Android.',
      },
      tv: {
        title: 'TV',
        description: 'Regardez sur votre TV connectée.',
      },
      rewards: {
        title: 'Récompenses',
        description: 'Cashback sur vos achats premium.',
      },
      extension: {
        title: 'Extension',
        description: 'Accédez à toutes les fonctionnalités.',
      },
      audio: {
        title: 'Audio',
        description: 'Écoutez sur tous vos appareils.',
      },
      gaming: {
        title: 'Gaming',
        description: 'Découvrez nos apps de jeux.',
      },
    },
  },
  footer: {
    brand: {
      description:
        "L'écosystème Skoleom réunit les boutiques audiovisuelles, les outils créateurs et les solutions business pour rendre les contenus interactifs et actionnables.",
    },
    socials: {
      label: 'Réseaux sociaux Skoleom',
    },
    shopPrompt: {
      prefix: 'Vous pouvez aussi faire vos achats dans',
      link: 'la boutique en ligne Skoleom',
    },
    copyright: 'Tous droits réservés.',
    modal: {
      close: 'Fermer le menu',
      title: 'Service non disponible',
      body: "Ce service n'est pas disponible dans votre région.",
    },
    columns: {
      about: 'À propos de Skoleom',
      stores: 'Boutiques audiovisuelles',
      everyone: 'Pour tous',
      pros: 'Pour les pros',
      group: 'Groupe',
      '1': 'À propos de Skoleom',
      '2': 'Boutiques audiovisuelles',
      '3': 'Pour tous',
      '4': 'Pour les pros',
      '5': 'Groupe',
    },
    links: {
      '101': 'Notre mission',
      '102': 'Notre technologie',
      '103': "L'écosystème Skoleom",
      '104': 'Programmes de financement',
      '105': 'Brevet Skoleom',
      '201': 'Explorer les boutiques',
      '202': 'Univers par catégorie',
      '203': 'Créer une boutique',
      '301': 'Comment ça marche',
      '302': 'Regarder et acheter',
      '303': 'Cashback & récompenses',
      '304': 'Skoleom Wallet',
      '305': 'Sécurité & confidentialité',
      '401': 'Solutions business',
      '402': 'Monétiser vos contenus',
      '403': 'Créer une boutique audiovisuelle',
      '404': 'Distribuer vos produits',
      '405': 'Publicité interactive',
      '406': 'API & intégrations',
      '407': 'Créer une Audiovisual Store Page',
      '501': 'Actualités',
      '502': 'Carrières',
      '503': 'Investisseurs',
      '504': 'Presse',
      '505': 'Contact',
      '506': 'Assistance',
    },
    legalLinks: {
      '901': 'Mentions légales',
      '902': 'CGU & CUV',
      '903': 'Confidentialité',
      '904': 'Divulgation des affiliés',
      '905': 'Préférences cookies',
      '906': 'Politique de remboursement',
    },
  },
  auth: {
    login: {
      title: 'Connexion',
      submit: 'Connexion',
    },
    register: {
      title: 'Créer un compte',
      submit: 'Créer un compte',
    },
  },
  dashboard: {
    user: {
      orders: 'Vos commandes',
    },
    admin: {
      title: 'Admin Console',
    },
  },
  home: {
    hero: {
      title: "Découvrez l'écosystème mondial de Skoleom Group",
      description: {
        intro: 'Skoleom Group révolutionne ',
        economy: "l'économie digitale",
        mid: " en permettant l'achat instantané au cœur des contenus audiovisuels. Connectée à plus de ",
        ott: '2000 plateformes OTT',
        join: ' et à ',
        web: 'des milliards de sites web',
        outro: ', notre technologie transforme chaque écran en point de vente interactif.',
      },
      download: 'Télécharger',
      application: "l'application",
      extension: "l'extension",
      watchVideo: 'Lire la vidéo',
      tagline1: 'CHAQUE VIDÉO DEVIENT UN VÉRITABLE MAGASIN IMMERSIF :',
      tagline2:
        "UNE EXPÉRIENCE FLUIDE, ÉLÉGANTE ET INTERACTIVE, OÙ L'ON DÉCOUVRE, TOUCHE ET ACHÈTE INSTANTANÉMENT CE QUE L'ON VOIT.",
    },
    skoletoonsHero: {
      kicker: 'Mode, beauté et lifestyle shoppable',
      title: "SkoleToon's",
      subtitle:
        'Une boutique audiovisuelle pensée pour transformer chaque inspiration mode en expérience interactive.',
      body: 'Découvrez un univers premium où les looks, les produits et les moments deviennent accessibles au bon instant.',
      discover: 'Découvrir',
      videoTitle: "Vidéo SkoleToon's",
      modalTitle: 'Boutique en cours de développement',
      modalBody:
        "La boutique SkoleToon's est encore en cours de développement. Elle sera disponible prochainement dans l'écosystème Skoleom.",
      modalClose: 'Fermer',
    },
    actions: {
      discoverMagic: 'Découvrez la magie',
    },
    welcome: {
      part1: 'Bienvenue dans le ',
      part2: 'futur du ',
      part3: 'retail media',
      part4: 'et du ',
      part5: 'transmédia',
    },
    page: {
      heroClose: 'Fermer',
      interactiveKicker: 'Une expérience interactive',
      interactiveSubtitle: "À l'intérieur de la vidéo.\nAu même moment.\nSans friction.",
      ecosystemKicker: 'Un univers. Des possibilités multiples.',
      ecosystemTitle: "L'écosystème\nSkoleom",
      ecosystemIntro:
        'Skoleom détecte ce que vous regardez et rend tout ce que vous voyez achetable, dans une expérience interactive inédite.',
      ecosystemStats: {
        products: 'de produits et services rendus shoppables en un seul toucher.',
        countries: "Pays connectés à l'écosystème.",
        detection: 'Pour détecter et activer ce que vous regardez.',
        alwaysOpen: 'Boutiques audiovisuelles toujours ouvertes.',
      },
      partnersTitle: 'Marques partenaires',
      boutiquesCta: 'Découvrez nos',
      boutiquesTitle: 'Boutiques Audiovisuelles',
      boutiquesSubtitle:
        'Un écosystème immersif. Plus de 50 applications web. Une seule limite : vos envies.',
      partnersSearchPlaceholder: 'Rechercher une marque (ex: Decathlon, Netflix, Intersport...)',
      partnersSearchAria: 'Rechercher une marque partenaire',
    },
    news: {
      badge: 'Nouveautés',
    },
    boutiques: {
      title: 'Découvrez nos Boutiques Audiovisuelles',
      searchPlaceholder: 'Rechercher un site Skoleom (ex: market, sport, ott...)',
    },
    sections: {
      welcome: 'Bienvenue dans le futur du retail media et du transmedia',
      how: 'Comment fonctionne Skoleom',
      moment: 'Chaque moment. Devient. Une opportunité.',
      interactive: 'Une expérience interactive',
      universe: 'Un univers. Plusieurs possibilités.',
      ecosystem: "L'écosystème Skoleom",
      partners: 'Marques partenaires',
    },
    descriptions: {
      detect:
        'Skoleom détecte ce que vous regardez et rend tout ce que vous voyez achetable, dans une expérience interactive inédite.',
    },
    stats: {
      storePages: 'pages Audiovisual Store par boutique.',
    },
  },
  public: {
    assistance: {
      categories: {
        users: 'Utilisateurs',
        solutions: 'Solutions',
        business: 'Entreprise',
        support: 'Support',
        chat: 'Chat',
      },
    },
    stores: {
      hero: {
        titleLine1: 'Découvrez nos',
        titleLine2: 'Boutiques audiovisuelles',
        subtitle:
          'Un écosystème immersif. Plus de 50 applications web. Une seule limite : votre imagination.',
      },
      search: {
        placeholder: 'Rechercher une boutique ou une marque',
        aria: 'Rechercher une boutique ou une marque',
      },
      sections: {
        official: 'Boutiques officielles',
        brands: 'Marques disponibles',
      },
      showcase: {
        kicker: 'Boutiques à la une',
        title: 'Univers phares',
        subtitle:
          'Sport, OTT, musique et gaming — un aperçu des boutiques audiovisuelles emblématiques.',
      },
      carousel: {
        kicker: 'Toutes les boutiques',
        title: 'Boutiques audiovisuelles',
        subtitle: 'Découvrez toutes les boutiques disponibles dans l’écosystème Skoleom.',
        searchPlaceholder: 'Rechercher une boutique (ex : sport, music, games...)',
        ariaLabel: 'Carrousel des boutiques audiovisuelles',
      },
    },
    howItWorks: {
      hero: {
        kicker: 'Comment ça marche',
        subtitle: 'Dans la vidéo. Au même moment. Sans friction.',
      },
      steps: {
        '01': {
          title: 'RECONNAISSANCE IA',
          desc: 'Notre IA identifie les produits, objets et moments dans n’importe quel contenu.',
        },
        '02': {
          title: 'CAPSULES INTELLIGENTES',
          desc: 'Des capsules interactives apparaissent en temps réel, en toute fluidité.',
        },
        '03': {
          title: 'UN SEUL CLIC',
          desc: 'Vous cliquez. Pas de redirection. Pas de friction. Juste une action instantanée.',
        },
        '04': {
          title: 'ACHAT EN CONTEXTE',
          desc: 'Finalisez votre achat sans quitter le contenu que vous aimez.',
        },
      },
      demo: {
        titlePrefix: 'Découvrez la',
        titleHighlight: 'magie',
        cta: 'Lancer la démo interactive',
      },
      showcase: {
        kicker: 'Univers grand public',
        title: 'Boutiques à la une',
        subtitle:
          'Découvrez des expériences interactives accessibles à tous, intégrées à vos contenus favoris.',
      },
      carousel: {
        kicker: 'Toutes les boutiques',
        title: 'Pour tous',
        subtitle: 'Toutes les boutiques grand public de l’écosystème Skoleom.',
        searchPlaceholder: 'Rechercher (ex : page, shop, magazine...)',
        ariaLabel: 'Carrousel des boutiques Pour tous',
      },
    },
    technology: {
      hero: {
        kicker: 'SeSync · La technologie',
        titlePrefix: 'Connecté à plus de',
        titleMiddle: 'et',
      },
      features: {
        seamless: "Une expérience d'achat transparente directement dans les vidéos",
        compatible: 'Compatible avec vos marques et créateurs préférés',
        secure: 'Sécurisé et conforme au RGPD',
      },
      download: "Télécharger l'extension",
      studio: {
        titleLine1: "L'outil de monétisation",
        titleLine2Prefix: 'le plus',
        titleLine2Highlight: 'puissant au monde',
        description:
          'ERP/DCM SaaS dédié à la gestion, la monétisation et l’analyse des contenus vidéo interactifs. Connecté à plus de 300 APIs. Contrôle total : ventes, audiences, campagnes, retargeting et performance produit.',
      },
    },
  },
  data: {
    showcase: {
      'av-sesports': {
        title: 'Skoleom SeSports',
        desc: 'Interactive sports universe: video-to-commerce, retail media and fan engagement. The group official audiovisual store for sports brands and federations.',
      },
      'av-watch': {
        title: 'Watch on Skoleom',
        desc: 'Skoleom shoppable OTT experience: movies, series and live streams where every visible product becomes accessible in one tap.',
      },
      'av-music': {
        title: 'Skoleom Music',
        desc: 'Label, artists and merchandising. The audiovisual store dedicated to music with direct purchase capsules in clips and concerts.',
      },
      'av-games': {
        title: 'Skoleom Games',
        desc: 'Gaming universe and in-game monetization. Proprietary IP, studio partnerships and interactive capsules embedded in content.',
      },
      'tous-sesync': {
        title: 'SeSync',
        desc: 'Skoleom synchronization engine: in-page purchasing and real-time multi-platform distribution.',
      },
      'tous-shop': {
        title: 'Skoleom Shop',
        desc: 'The group official marketplace: products, merchandise and limited editions available directly from your favorite content.',
      },
      'tous-secontent': {
        title: 'SeContent Creation',
        desc: 'Creation and orchestration of interactive capsules. Intuitive tools to turn any content into a shoppable experience.',
      },
      'tous-magazine': {
        title: 'Skoleom Magazine',
        desc: 'Media and interactive formats: news, features and reports enriched with inline shopping capsules.',
      },
    },
    boutiques: {
      'av-sesports': {
        name: 'Skoleom SeSports',
        description: 'Sports universe and retail media.',
      },
      'av-cares': {
        name: 'Skoleom Cares',
        description: 'Social impact and dedicated programs.',
      },
      'av-games': {
        name: 'Skoleom Games',
        description: 'Games, IP and in-game monetization.',
      },
      'av-watch': {
        name: 'Watch on Skoleom',
        description: 'Shoppable OTT content and live experiences.',
      },
      'av-music': {
        name: 'Skoleom Music',
        description: 'Music, artists and direct merchandising.',
      },
      'tous-page': {
        name: 'Skoleom Page',
        description: 'Skoleom entry point and discovery.',
      },
      'tous-sesync': {
        name: 'SeSync',
        description: 'In-page purchasing and distribution.',
      },
      'tous-shop': {
        name: 'Skoleom Shop',
        description: 'Official marketplace for the ecosystem.',
      },
      'tous-secontent': {
        name: 'SeContent Creation',
        description: 'Create and orchestrate interactive capsules.',
      },
      'tous-magazine': {
        name: 'Skoleom Magazine',
        description: 'Interactive media, news and reports.',
      },
    },
  },
} satisfies LocaleResource;

export default resource;
